import { FC, ReactNode, useEffect } from "react";
import { motion } from "framer-motion";

import "./Layout.css";

import { RouteKey, RouteTitle } from "../configs/routes";
import { Link } from "./Link";
import { useCache } from "../utils/useCache";
import { Install } from "./Install";

interface LayoutProps {
  route: RouteKey;
  children: ReactNode;
}

const isNotIndex = (route: RouteKey) => route !== RouteKey.INDEX;

const renderLayoutLinks = (route: RouteKey) => {
  if (isNotIndex(route)) {
    return null;
  }

  return null;
}

export const Layout: FC<LayoutProps> = ({ route, children }) => {
  const { previousRouteTitle, setPreviousRouteTitle, nextStepAnimationSkip } = useCache();

  const routeTitle = RouteTitle[route];

  useEffect(() => {
    setPreviousRouteTitle && setPreviousRouteTitle(routeTitle);
  }, [routeTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasHeader = isNotIndex(route);
  const isInitialRun = !previousRouteTitle && !routeTitle;

  const headerHidden = {
    y: -50,
    opacity: 0
  };

  const headerVisible = {
    y: 0,
    opacity: 1
  }

  const getHeaderStyleFrom = () => {
    if (nextStepAnimationSkip) {
      return hasHeader ? headerVisible : headerHidden;
    }

    return hasHeader || isInitialRun ? headerHidden : headerVisible;
  }

  const getHeaderStyleTo = () => {
    if (nextStepAnimationSkip) {
      return hasHeader ? headerVisible : headerHidden;
    }

    return hasHeader && !isInitialRun ? headerVisible : headerHidden;
  }

  const layoutHidden = {
    y: 300,
    opacity: 0,
    scale: 0
  };

  const layoutVisible = {
    y: 0,
    scale: 1,
    opacity: 1
  }

  const getLayoutStyleFrom = () => {
    if (nextStepAnimationSkip) {
      return layoutVisible;
    }

    if (RouteKey.HISTORY) {
      return layoutHidden;
    }

    return layoutVisible;
  }

  const getLayoutStyleTo = () => {
    return layoutVisible;
  }

  return (
    <div className={`layout layout--${route.toLowerCase()}`} >
      <motion.div
        className="layout__header"
        initial={getHeaderStyleFrom()}
        animate={getHeaderStyleTo()}
        transition={{
          duration: 0.7,
          ease: [0.19, 1, 0.22, 1]
        }}
      >
        <div className="layout__header-back">
          <Link route={RouteKey.INDEX}>
            ←
          </Link>
        </div>
        <div className="layout__header-title">
          {hasHeader ? routeTitle : previousRouteTitle}
        </div>
        <div className="layout__header-links">
          {renderLayoutLinks(route)}
        </div>
      </motion.div>
      <motion.div
        className="layout__content-wrapper"
        initial={getLayoutStyleFrom()}
        animate={getLayoutStyleTo()}
        transition={{
          duration: 0.3,
          ease: [0.19, 1, 0.22, 1]
        }}
      >
        <div className="layout__content">
          {children}
        </div>
      </motion.div>
      <Install />
    </div>
  )
}